import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutData } from "@/mocks/about";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9pc6r0senbniolesslg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "提交失败，请稍后再试";
        setFormError(msg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请稍后再试");
      setFormStatus("error");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于布丁漫画网",
    description: "布丁漫画网是国内领先的免费下拉式漫画阅读平台，了解我们的品牌故事和使命。",
    url: "https://www.puddingcomic.com/about",
    mainEntity: {
      "@type": "Organization",
      name: "布丁漫画网",
      description: aboutData.introduction,
      slogan: aboutData.slogan,
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      address: {
        "@type": "PostalAddress",
        addressLocality: "上海",
        addressRegion: "浦东新区",
        streetAddress: aboutData.contact.address,
      },
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutData.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>
        </div>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于布丁漫画网
            </h1>
            <p className="text-lg md:text-xl text-primary-600 font-heading mb-6">
              {aboutData.slogan}
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {aboutData.introduction}
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              {aboutData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
              我们的故事
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              {aboutData.mission}
            </p>
            <div className="max-w-3xl mx-auto">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutData.story}
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
              为什么选择我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              六大优势，定义漫画阅读新体验
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutData.features.map((feature) => (
                <div
                  key={feature.title}
                  className="flex gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              听听读者们怎么说
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {aboutData.testimonials.map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {testimonial.content}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground-900">
                      {testimonial.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              您可能关心的问题
            </p>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutData.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900">
                      {faq.question}
                    </span>
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                      <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议，欢迎随时联系我们
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
              {[
                {
                  icon: "ri-mail-line",
                  label: "电子邮箱",
                  value: aboutData.contact.email,
                },
                {
                  icon: "ri-phone-line",
                  label: "客服电话",
                  value: aboutData.contact.phone,
                },
                {
                  icon: "ri-wechat-line",
                  label: "微信公众号",
                  value: aboutData.contact.wechat,
                },
                {
                  icon: "ri-map-pin-line",
                  label: "公司地址",
                  value: aboutData.contact.address,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6 text-center">
                发送消息
              </h3>
              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    消息内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400 resize-none"
                    placeholder="请输入您的消息内容（最多500字）"
                  ></textarea>
                </div>
                <div className="sr-only-form">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
                <div className="text-center">
                  <button
                    type="submit"
                    className="px-8 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                </div>
                {formStatus === "success" && (
                  <p className="text-center text-sm text-green-600">
                    消息发送成功！我们会尽快回复您。
                  </p>
                )}
                {formStatus === "error" && (
                  <p className="text-center text-sm text-red-500">
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}